import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, TextField, MenuItem, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { api } from '../../../api';
import { localGet } from '../../../utils/localStorageUtils';
import { removedNamesForUpdate } from '../../../utils/utilsApi';

/**
 * Identity approval (Oct 2026, FEAT-022 / D4) — each school names the person with the final say
 * on revealing a student's identity (e.g. the Principal) and a backup for when they're out
 * (time off / Out of Office). A responder's de-anonymization request is escalated to them
 * automatically, and ONLY these two can approve or deny it. The server enforces all of this;
 * only district administrators (and DiscloSure admins) can change the selection.
 */
const CAN_DESIGNATE = ['disclosureAdmin', 'districtAdmin'];
const DESIGNATABLE = ['schoolAdmin', 'districtAdmin', 'responder'];
const PERMISSION_LABELS = { schoolAdmin: 'School Admin', districtAdmin: 'District Admin', responder: 'Responder' };

const nameOf = (u) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.id;

const IdentityApproverPanel = ({ data }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approver, setApprover] = useState(data?.deAnonApproverId || '');
  const [backup, setBackup] = useState(data?.deAnonBackupApproverId || '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      setUser(await localGet('user', true));
      try {
        const res = await api('get', {
          filters: JSON.stringify([{ key: 'school', value: data.id, expression: '=' }]),
          pageSize: 200,
        }, 'users');
        const rows = (res?.data?.data || []).filter((u) => DESIGNATABLE.includes(u.permission));
        rows.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
        if (mounted) setStaff(rows);
      } catch (e) {
        // leave the list empty; the current selection still shows by id
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [data.id]);

  const canEdit = Boolean(user && CAN_DESIGNATE.includes(user.permission));
  const byId = useMemo(() => Object.fromEntries(staff.map((u) => [u.id, u])), [staff]);
  const label = (id) => (id ? (byId[id] ? nameOf(byId[id]) : 'Selected staff member') : 'Not set');
  const dirty = approver !== (data?.deAnonApproverId || '') || backup !== (data?.deAnonBackupApproverId || '');

  const save = async () => {
    setBusy(true); setError(''); setMessage('');
    if (approver && backup && approver === backup) {
      setError('The backup must be a different person from the approver.');
      setBusy(false);
      return;
    }
    const updated = { ...data, deAnonApproverId: approver, deAnonBackupApproverId: backup };
    const record = await removedNamesForUpdate({ ...updated });
    const res = await api('patch', record, 'schools');
    if (res?.status === 200) {
      setMessage('Saved. Identity requests at this school now go to the people above.');
      navigate('.', { replace: true, state: updated });
    } else {
      setError(res?.data?.message || 'Could not save. Please try again.');
    }
    setBusy(false);
  };

  const style = {
    container: {
      display: 'flex', flexDirection: 'column', marginLeft: '60px', marginRight: '60px', marginTop: '40px',
      width: 'auto', paddingLeft: '0px !important', border: `1px solid ${theme.palette.custom?.gray || '#ccc'}`, borderRadius: '4px',
    },
    header: { borderBottom: `1px solid ${theme.palette.custom?.gray || '#ccc'}` },
    title: { fontSize: '20px', fontWeight: '700', marginTop: '20px', marginBottom: '20px', marginLeft: '20px' },
    body: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
    text: { fontSize: '14px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 },
    row: { display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' },
    field: {
      minWidth: '280px',
      '& .MuiInputBase-input': { color: theme.palette.custom?.black || '#222' },
    },
    readLabel: { fontSize: '13px', color: theme.palette.text?.secondary || '#666' },
    readValue: { fontSize: '16px', fontWeight: 600, color: theme.palette.custom?.black || '#222' },
    ok: { fontSize: '13px', color: theme.palette.success?.main || '#2e7d32' },
    error: { fontSize: '13px', color: theme.palette.error?.main || '#d32f2f' },
  };

  const picker = (value, setValue, title, help) => (
    <TextField
      select
      label={title}
      helperText={help}
      value={value}
      onChange={(e) => { setValue(e.target.value); setMessage(''); }}
      sx={style.field}
      disabled={busy || loading}
    >
      <MenuItem value="">Not set</MenuItem>
      {staff.map((u) => (
        <MenuItem key={u.id} value={u.id}>
          {nameOf(u)}{PERMISSION_LABELS[u.permission] ? ` · ${PERMISSION_LABELS[u.permission]}` : ''}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Container sx={style.container}>
      <Box sx={style.header}>
        <Typography sx={style.title}>Identity approval</Typography>
      </Box>
      <Box sx={style.body}>
        <Typography sx={style.text}>
          When a responder asks to reveal an anonymous student's identity, the case is escalated
          automatically to this school's identity approver, or to the backup while the approver is
          on time off or out of office. Only these two people can approve or deny the request.
        </Typography>
        {loading ? <CircularProgress size={22} /> : canEdit ? (
          <>
            <Box sx={style.row}>
              {picker(approver, setApprover, 'Identity approver', 'Highest authority at the school, e.g. the Principal')}
              {picker(backup, setBackup, 'Backup approver', 'Used while the approver is out, e.g. the Assistant Principal')}
            </Box>
            <Box>
              <Button variant="contained" disabled={busy || !dirty} onClick={save}>
                {busy ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={style.row}>
            <Box>
              <Typography sx={style.readLabel}>Identity approver</Typography>
              <Typography sx={style.readValue}>{label(data?.deAnonApproverId)}</Typography>
            </Box>
            <Box>
              <Typography sx={style.readLabel}>Backup approver</Typography>
              <Typography sx={style.readValue}>{label(data?.deAnonBackupApproverId)}</Typography>
            </Box>
          </Box>
        )}
        {!loading && !data?.deAnonApproverId && !data?.deAnonBackupApproverId ? (
          <Typography sx={style.text}>
            Not set yet: until an approver is chosen, requests go to all School Admins at this school.
          </Typography>
        ) : null}
        {message ? <Typography sx={style.ok}>{message}</Typography> : null}
        {error ? <Typography sx={style.error}>{error}</Typography> : null}
      </Box>
    </Container>
  );
};

export default IdentityApproverPanel;
